import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Order, CustomOptionChoice } from '../types';
import { orderApi } from '../services/api';
import SEO from '../components/SEO';
import { format } from 'date-fns';
import { ArrowLeft } from 'lucide-react';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (id && user) {
      fetchOrder();
    }
  }, [id, user]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const orderData = await orderApi.getById(id!);
      
      // Security check: ensure user can only view their own orders unless admin
      if (user?.role !== 'admin' && orderData.customerEmail !== user?.email) {
        setError('You do not have permission to view this order.');
        setOrder(null);
      } else {
        setOrder(orderData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order details');
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'pending_payment': 'bg-orange-100 text-orange-800',
      'paid': 'bg-blue-100 text-blue-800',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status: Order['status'] | Order['paymentStatus']) => {
    if (!status) return '';
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (!user) {
    return (
      <>
        <SEO 
          title="Order Details"
          description="Please log in to view order details at Keystone Sign Printing."
        />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-gray-600">Please log in to view order details.</p>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-keystone-gold"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">
          {error}
        </div>
        <Link 
          to="/orders" 
          className="inline-flex items-center gap-2 text-keystone-gold hover:text-keystone-gold-600"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Order not found.</p>
          <Link 
            to="/orders" 
            className="inline-flex items-center gap-2 text-keystone-gold hover:text-keystone-gold-600"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`Order ${order.id}`}
        description={`View details for order ${order.id} at Keystone Sign Printing.`}
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Order Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Order {order.id}</h1>
              <p className="text-gray-600">
                Placed on {format(new Date(order.createdAt), 'MMMM d, yyyy h:mm a')}
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
              {formatStatus(order.status)}
            </span>
          </div>
          
          {order.estimatedCompletion && (
            <p className="text-sm text-gray-600">
              Estimated Completion: {format(new Date(order.estimatedCompletion), 'MMMM d, yyyy')}
            </p>
          )}
          
          {order.notes && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Notes:</p>
              <p className="text-gray-600">{order.notes}</p>
            </div>
          )}
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Order Items</h2>
          <div className="space-y-4">
            {order.items.map((item, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div>
                  <h3 className="font-medium text-lg">{item.product.name}</h3>
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    {item.width && item.height && (
                      <p>Size: {item.width}" × {item.height}"</p>
                    )}
                    {item.material && (
                      <p>Material: {item.product.materials?.find(m => m.id === item.material)?.name || item.material}</p>
                    )}
                    {item.finishing && (
                      <p>Finishing: {item.product.finishingOptions?.find(f => f.id === item.finishing)?.name || item.finishing}</p>
                    )}
                    {item.colorSpec && (
                      <p>Color: {item.product.colorSpecs?.find(c => c.id === item.colorSpec)?.name || item.colorSpec}</p>
                    )}
                    {item.customOptions && Object.keys(item.customOptions).length > 0 && (
                      <>
                        {Object.entries(item.customOptions).map(([optionId, choiceId]) => {
                          const customOption = item.product.customOptions?.find(opt => opt.id === optionId);
                          const choice = customOption?.options.find((c: CustomOptionChoice) => c.id === choiceId);
                          return (
                            <p key={optionId}>
                              {customOption?.name || optionId}: {choice?.name || choiceId}
                            </p>
                          );
                        })}
                      </>
                    )}
                    {item.customText && <p>Custom Text: {item.customText}</p>}
                    <p className="font-medium text-gray-700">Quantity: {item.quantity}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Customer Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-medium">
                {order.customerInfo.firstName} {order.customerInfo.lastName}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{order.customerInfo.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-medium">{order.customerInfo.phone}</p>
            </div>
            {order.customerInfo.company && (
              <div>
                <p className="text-sm text-gray-600">Company</p>
                <p className="font-medium">{order.customerInfo.company}</p>
              </div>
            )}
          </div>
          
          {/* Billing Address */}
          <div className="mt-6">
            <h3 className="font-medium mb-2">Billing Address</h3>
            <p className="text-gray-600">
              {order.customerInfo.address}<br />
              {order.customerInfo.city}, {order.customerInfo.state} {order.customerInfo.zipCode}
            </p>
          </div>
        </div>

        {/* Shipping Address */}
        {order.shippingAddress && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Shipping Address</h2>
            <p className="text-gray-600">
              {order.shippingAddress.line1}<br />
              {order.shippingAddress.line2 && (
                <>{order.shippingAddress.line2}<br /></>
              )}
              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postal_code}<br />
              {order.shippingAddress.country}
            </p>
          </div>
        )}

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Payment Information</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-medium">${(order.totalPrice - (order.shipping?.amount_total || 0)).toFixed(2)}</span>
            </div>
            {order.shipping && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">${(order.shipping.amount_subtotal / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">${(order.shipping.amount_tax / 100).toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>Total</span>
                <span>${order.totalPrice.toFixed(2)}</span>
              </div>
            </div>
            {order.paymentStatus && (
              <p className="text-sm text-gray-600 mt-2">
                Payment Status: <span className="font-medium">{formatStatus(order.paymentStatus)}</span>
              </p>
            )}
            {order.paymentMethod && (
              <p className="text-sm text-gray-600">
                Payment Method: <span className="font-medium">{order.paymentMethod}</span>
              </p>
            )}
            {order.paidAt && (
              <p className="text-sm text-gray-600">
                Paid on: <span className="font-medium">
                  {format(new Date(order.paidAt), 'MMMM d, yyyy h:mm a')}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetail;